import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Grid,
  Paper,
  Divider,
  Chip,
  List,
  ListItem,
  ListItemText,
  Avatar,
  Button,
  CircularProgress,
} from "@mui/material";
import {
  Person,
  Phone,
  Home,
  Cake,
  LocalShipping,
  Store,
  Badge,
  Refresh,
} from "@mui/icons-material";
import { IUser } from "../types"; // Ajuste o caminho conforme necessário

interface UserProfileProps {
  data: IUser | null; // Permite que o componente receba dados nulos
}

const UserProfile = (props: UserProfileProps) => {
  const [apiData, setApiData] = useState<any>(null);
  const [loadingApi, setLoadingApi] = useState(false);

  // Função para buscar dados do usuário na API externa
  const fetchUserApiData = async (cpfCnpj: string) => {
    if (!cpfCnpj || cpfCnpj.trim() === "") return;

    setLoadingApi(true);
    try {
      const API_URL =
        process.env.REACT_APP_API_FP_BE || "http://localhost:5000/api";

      const response = await fetch(
        `${API_URL}/external/pessoas/search?cpfcnpj=${cpfCnpj}&cliente=false&fornecedor=false`
      );

      if (!response.ok) {
        throw new Error(`Erro na API: ${response.status}`);
      }

      const data = await response.json();

      if (data && data.length > 0) {
        setApiData(data[0]); // Pegar o primeiro resultado
      } else {
        setApiData(null);
      }
    } catch (error) {
      setApiData(null);
    } finally {
      setLoadingApi(false);
    }
  };

  // useEffect para buscar dados do usuário quando houver CPF disponível
  useEffect(() => {
    const cpfCnpj = props.data?.CNPJ_CPF;
    if (cpfCnpj) {
      fetchUserApiData(cpfCnpj);
    }
  }, [props.data?.CNPJ_CPF]);

  // Função para atualizar os dados manualmente
  const handleRefresh = () => {
    const cpfCnpj = props.data?.CNPJ_CPF;
    if (cpfCnpj) {
      fetchUserApiData(cpfCnpj);
    }
  };
  return (
    <Paper sx={{ p: 3, borderRadius: 2, margin: "auto" }}>
      {/* Cabeçalho */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Avatar sx={{ width: 80, height: 80, bgcolor: "primary.main" }}>
            <Person sx={{ fontSize: 40 }} />
          </Avatar>
          <Box>
            <Typography variant="h6" component="h1">
              {props.data?.NomeFantasia || props.data?.RazaoSocial}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={
              loadingApi ? <CircularProgress size={16} /> : <Refresh />
            }
            onClick={handleRefresh}
            disabled={loadingApi}
          >
            {loadingApi ? "Atualizando..." : "Atualizar"}
          </Button>
        </Box>
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Informações principais */}
      <Grid container spacing={3}>
        {/* Coluna 1 - Dados pessoais */}
        <Grid size={{ xs: 12, sm: 12, md: 6 }}>
          <Typography
            variant="h6"
            gutterBottom
            sx={{ display: "flex", alignItems: "center", gap: 1 }}
          >
            <Badge color="primary" /> Informações Pessoais
            {loadingApi && (
              <CircularProgress size={16} sx={{ ml: 1 }} color="primary" />
            )}
            {apiData && (
              <Typography
                variant="caption"
                color="success.main"
                sx={{ ml: 1, fontStyle: "italic" }}
              >
                (Dados atualizados do ERP)
              </Typography>
            )}
          </Typography>

          <List dense>
            <ListItem>
              <ListItemText
                primary="CPF/CNPJ"
                secondary={
                  apiData?.CNPJ_CPF || props.data?.CNPJ_CPF || "Não informado"
                }
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Data de Nascimento"
                secondary={
                  apiData?.DataNascimento.slice(0, -8) ||
                  String(props.data?.DataNascimento) ||
                  "Não informado"
                }
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Email Principal"
                secondary={
                  apiData?.Email || props.data?.Email || "Não informado"
                }
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Telefone"
                secondary={(() => {
                  const telefones = [
                    ...(apiData?.TelefonesSecundarios ||
                      props.data?.TelefonesSecundarios ||
                      []),
                    apiData?.Telefone || props.data?.Telefone,
                    apiData?.Celular || props.data?.Celular,
                  ].filter(Boolean);

                  return telefones.length > 0 ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Phone fontSize="small" color="action" />
                      {telefones.join(", ")}
                    </Box>
                  ) : (
                    "Não informado"
                  );
                })()}
              />
            </ListItem>
          </List>
        </Grid>

        {/* Coluna 2 - Endereço */}
        <Grid size={{ xs: 12, sm: 12, md: 6 }}>
          <Typography
            variant="h6"
            gutterBottom
            sx={{ display: "flex", alignItems: "center", gap: 1 }}
          >
            <Home color="primary" /> Endereço Padrão
            {apiData && (
              <Typography
                variant="caption"
                color="success.main"
                sx={{ ml: 1, fontStyle: "italic" }}
              >
                (Dados atualizados do ERP)
              </Typography>
            )}
          </Typography>

          <List dense>
            <ListItem>
              <ListItemText
                primary="Logradouro"
                secondary={
                  apiData?.EnderecoPadrao?.Logradouro || apiData?.Logradouro
                    ? `${
                        apiData?.EnderecoPadrao?.Logradouro ||
                        apiData?.Logradouro
                      }${
                        apiData?.EnderecoPadrao?.Numero ||
                        apiData?.LogradouroNumero
                          ? `, ${
                              apiData?.EnderecoPadrao?.Numero ||
                              apiData?.LogradouroNumero
                            }`
                          : ""
                      }`
                    : props.data?.EnderecoPadrao?.Logradouro &&
                        props.data?.EnderecoPadrao?.Numero
                      ? `${props.data?.EnderecoPadrao?.Logradouro}, ${props.data?.EnderecoPadrao?.Numero}`
                      : "Não informado"
                }
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Bairro"
                secondary={
                  apiData?.EnderecoPadrao?.Bairro ||
                  apiData?.Bairro ||
                  props.data?.EnderecoPadrao?.Bairro ||
                  "Não informado"
                }
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Cidade/UF"
                secondary={
                  apiData?.EnderecoPadrao?.Cidade && apiData?.EnderecoPadrao?.Uf
                    ? `${apiData.EnderecoPadrao.Cidade}/${apiData.EnderecoPadrao.Uf}`
                    : apiData?.Cidade && apiData?.UF
                      ? `${apiData.Cidade}/${apiData.UF}`
                      : props.data?.EnderecoPadrao?.Cidade &&
                          props.data?.EnderecoPadrao?.Uf
                        ? `${props.data?.EnderecoPadrao?.Cidade}/${props.data?.EnderecoPadrao?.Uf}`
                        : "Não informado"
                }
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="CEP"
                secondary={
                  apiData?.EnderecoPadrao?.CEP ||
                  apiData?.CEP ||
                  props.data?.EnderecoPadrao?.CEP ||
                  "Não informado"
                }
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="País"
                secondary={
                  apiData?.EnderecoPadrao?.Pais ||
                  props.data?.EnderecoPadrao?.Pais ||
                  "Brasil"
                }
              />
            </ListItem>
          </List>
        </Grid>
      </Grid>

      <Divider sx={{ my: 2 }} />

      {/* Status e categorias */}
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 12, md: 6 }}>
          <Typography
            variant="h6"
            gutterBottom
            sx={{ display: "flex", alignItems: "center", gap: 1 }}
          >
            <Store color="primary" /> Categorias
          </Typography>

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {props.data?.Cliente && (
              <Chip label="Cliente" color="primary" icon={<Person />} />
            )}
            {props.data?.Tecnico && <Chip label="Técnico" color="secondary" />}
            {props.data?.Vendedor && <Chip label="Vendedor" />}
            {props.data?.Transportadora && (
              <Chip label="Transportadora" icon={<LocalShipping />} />
            )}
            {props.data?.Fonecedor && <Chip label="Fornecedor" />}
            {props.data?.Representada && <Chip label="Representada" />}
          </Box>
        </Grid>

        <Grid size={{ xs: 12, sm: 12, md: 6 }}>
          <Typography
            variant="h6"
            gutterBottom
            sx={{ display: "flex", alignItems: "center", gap: 1 }}
          >
            <Cake color="primary" /> Informações Adicionais
          </Typography>

          <List dense>
            <ListItem>
              <ListItemText
                primary="Última Alteração"
                secondary={String(props.data?.UltimaAlteracao)}
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Data de Cadastro"
                secondary={String(props.data?.DataCadastro) || "Não informada"}
              />
            </ListItem>
            {/* <ListItem>
              <ListItemText
                primary="Valor Mínimo de Compra"
                secondary={`R$ ${props.data?.ValorMinimoCompra?.toFixed(2)}`}
              />
            </ListItem> */}
          </List>
        </Grid>
      </Grid>

      {/* Seção para endereços de entrega quando existirem */}
      {props.data?.EnderecosEntrega &&
        props.data?.EnderecosEntrega.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography
              variant="h6"
              gutterBottom
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              <LocalShipping color="primary" /> Endereços de Entrega
            </Typography>
            <Grid container spacing={2}>
              {props.data?.EnderecosEntrega.map(
                (endereco: any, index: number) => (
                  <Grid size={{ xs: 12, sm: 12, md: 6 }} key={index}>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Typography variant="subtitle1">
                        Endereço {index + 1}
                      </Typography>
                      <Typography>
                        {endereco.Logradouro}, {endereco.Numero}
                      </Typography>
                      <Typography>
                        {endereco.Bairro}, {endereco.Cidade}/{endereco.Uf}
                      </Typography>
                      <Typography>CEP: {endereco.CEP}</Typography>
                    </Paper>
                  </Grid>
                )
              )}
            </Grid>
          </>
        )}
    </Paper>
  );
};

export default UserProfile;
